import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './PartnersPage.css';

export default function PartnersPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', company: '', email: '', phone: '', type: 'oficina' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handlePhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    let masked = digits;
    if (digits.length > 2) masked = `(${digits.slice(0,2)}) ${digits.slice(2)}`;
    if (digits.length > 7) masked = `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
    setFormData({ ...formData, phone: masked });
  };
  const rootRef = useRef(null);

  useEffect(() => {
    // Scroll reveal
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.15 });
    rootRef.current?.querySelectorAll('.reveal').forEach(el => io.observe(el));

    // Header scroll
    const h = rootRef.current?.querySelector('.pp-header');
    const onScroll = () => h?.classList.toggle('scrolled', window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSending(true);
    try {
      const r = await fetch((process.env.REACT_APP_API_URL || 'https://nico-548x.onrender.com/api') + '/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erro ao enviar');
      setSent(true);
    } catch (err) {
      setFormError(err.message || 'Erro ao enviar. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div ref={rootRef} className="pp-root">
      {/* ── Header ── */}
      <header className="pp-header">
        <div className="pp-header-inner">
          <img src="/nico-1.png" alt="NICO" className="pp-logo" onClick={() => navigate('/')} />
          <nav className="pp-nav">
            <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Sou motorista</a>
            <button className="pp-nav-cta" onClick={() => scrollTo('pp-contato')}>Falar com o time</button>
          </nav>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="pp-hero">
        <div className="pp-hero-inner">
          <span className="pp-badge reveal">Oficinas · Frotas · Empresas</span>
          <h1 className="reveal">Cresça junto com a<br/>garagem digital do Brasil</h1>
          <p className="reveal">Ferramentas de gestão para oficinas, controle completo para frotas a partir de 3 veículos e parcerias com empresas que querem estar dentro do Nico.</p>
          <div className="pp-hero-btns reveal">
            <button className="pp-btn-primary" onClick={() => scrollTo('pp-planos')}>Ver planos →</button>
            <button className="pp-btn-outline" onClick={() => scrollTo('pp-contato')}>Quero uma parceria</button>
          </div>
        </div>
      </section>

      {/* ── Segmentos ── */}
      <section className="pp-section pp-segments">
        <div className="pp-container">
          <div className="pp-segments-grid">
            {[
              { icon: '🔧', tag: 'Oficinas', tagColor: '#e97117', title: 'Plano Oficina Pro', desc: 'Agenda, orçamento, histórico do cliente e retorno automático de revisão.' },
              { icon: '🚛', tag: 'Frotas', tagColor: '#14562e', title: 'Gestão de frota', desc: 'A partir de 3 veículos: custos, alertas e documentação em um painel só.' },
              { icon: '🤝', tag: 'Empresas', tagColor: '#c94a2f', title: 'Parcerias', desc: 'Seguradoras, postos, pedágio e assistência integrados ao app do motorista.' },
            ].map((s, i) => (
              <div key={i} className="pp-segment-card reveal">
                <div className="pp-segment-icon">{s.icon}</div>
                <span className="pp-segment-tag" style={{ color: s.tagColor }}>{s.tag.toUpperCase()}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Planos ── */}
      <section className="pp-section pp-plans" id="pp-planos">
        <div className="pp-container">
          <h2 className="pp-section-title reveal">Planos para oficinas e frotas</h2>
          <p className="pp-section-sub reveal">A página simples de oficina continua gratuita. Os planos abaixo são para quem quer gestão, agenda e mais clientes.</p>

          <div className="pp-plans-grid">
            {/* Oficina Free */}
            <div className="pp-plan-card reveal">
              <h3>Oficina Free</h3>
              <div className="pp-plan-price">
                <span className="pp-price-value">R$ 0</span>
                <span className="pp-price-period">para sempre</span>
              </div>
              <p className="pp-plan-desc">Página simples de oficina e registro de serviços.</p>
              <ul className="pp-plan-features">
                <li>Perfil da oficina</li>
                <li>Registro de serviços</li>
                <li>Histórico enviado ao cliente</li>
              </ul>
              <button className="pp-plan-btn" onClick={() => navigate('/login?tab=register&pro=true')}>Começar grátis</button>
            </div>

            {/* Oficina Pro */}
            <div className="pp-plan-card pp-plan-featured reveal">
              <span className="pp-plan-badge">Mais escolhido</span>
              <h3>Oficina Pro</h3>
              <div className="pp-plan-price">
                <div className="pp-price-old-row">
                  <span className="pp-price-old">R$ 149</span>
                  <span className="pp-price-save">-33%</span>
                </div>
                <div className="pp-price-new-row">
                  <span className="pp-price-value">R$ 99,90</span>
                  <span className="pp-price-period">/mês</span>
                </div>
                <span className="pp-price-promo-note">*Preço especial, válido no primeiro ano.</span>
              </div>
              <p className="pp-plan-desc">Para quem quer encher a agenda e fidelizar o cliente.</p>
              <ul className="pp-plan-features pp-plan-features-green">
                <li>Agenda e orçamentos</li>
                <li>Lembretes automáticos de retorno</li>
                <li>Destaque na busca do app</li>
                <li>Relatórios de faturamento</li>
              </ul>
              <button className="pp-plan-btn pp-plan-btn-primary" onClick={() => scrollTo('pp-contato')}>Falar com vendas</button>
            </div>

            {/* Frota */}
            <div className="pp-plan-card reveal">
              <h3>Frota</h3>
              <div className="pp-plan-price">
                <div className="pp-price-old-row">
                  <span className="pp-price-old">R$ 19</span>
                  <span className="pp-price-save">-48%</span>
                </div>
                <div className="pp-price-new-row">
                  <span className="pp-price-value">R$ 9,90</span>
                  <span className="pp-price-period">/veículo por mês</span>
                </div>
                <span className="pp-price-promo-note">*Preço especial, válido no primeiro ano.</span>
              </div>
              <p className="pp-plan-desc">A partir de 3 veículos, com painel de gestão.</p>
              <ul className="pp-plan-features">
                <li>Painel multiveículo</li>
                <li>Custos por veículo e período</li>
                <li>Alertas e documentação</li>
                <li>Usuários e permissões</li>
              </ul>
              <button className="pp-plan-btn" onClick={() => scrollTo('pp-contato')}>Simular frota</button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Parceiros ── */}
      <section className="pp-section pp-partners">
        <div className="pp-container">
          <div className="pp-partners-grid">
            <div className="reveal">
              <span className="pp-eyebrow">🤝 Empresas Parceiras</span>
              <h2>Coloque seu serviço dentro<br/>da rotina do motorista</h2>
              <p>Seguros, assistência 24h, baterias, pneus, combustível e pedágio: o Nico conecta sua empresa ao momento em que o cliente precisa do serviço.</p>
            </div>
            <div className="pp-partners-benefits">
              {[
                { icon: '🎯', title: 'Base qualificada', desc: 'Motoristas com veículo cadastrado e histórico ativo.' },
                { icon: '⏰', title: 'Oferta no momento certo', desc: 'Sua oferta aparece quando o serviço é necessário.' },
                { icon: '📊', title: 'Performance clara', desc: 'Relatórios de cliques, leads e conversão.' },
              ].map((b, i) => (
                <div key={i} className="pp-benefit-card reveal">
                  <div className="pp-benefit-icon">{b.icon}</div>
                  <div>
                    <h4>{b.title}</h4>
                    <p>{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Contato ── */}
      <section className="pp-section pp-contact" id="pp-contato">
        <div className="pp-container pp-contact-inner">
          <h2 className="pp-section-title reveal">Fale com o time do Nico</h2>
          <p className="pp-section-sub reveal">Conte o seu cenário e montamos a melhor configuração para oficina, frota ou parceria.</p>

          {sent ? (
            <div className="pp-contact-success reveal in">
              <div className="pp-success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#14562e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <h3>Recebemos seu contato!</h3>
              <p>Nosso time vai analisar o seu cenário e entrar em contato em até 24 horas pelo email <strong>{formData.email}</strong>.</p>
              <div className="pp-success-actions">
                <button className="pp-btn-primary" onClick={() => navigate('/')}>Conhecer o NICO →</button>
              </div>
            </div>
          ) : (
            <form className="pp-contact-form reveal" onSubmit={handleSubmit}>
              {formError && <div className="pp-form-error">{formError}</div>}
              <div className="pp-form-row">
                <input placeholder="Nome" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                <input placeholder="Empresa" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} />
              </div>
              <div className="pp-form-row">
                <input type="email" placeholder="E-mail" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required />
                <input placeholder="Telefone" value={formData.phone} onChange={e => handlePhone(e.target.value)} inputMode="tel" />
              </div>
              <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                <option value="oficina">Sou oficina</option>
                <option value="frota">Tenho frota</option>
                <option value="parceiro">Quero ser parceiro</option>
                <option value="outro">Outro</option>
              </select>
              <button type="submit" className="pp-btn-primary pp-btn-full" disabled={sending}>
                {sending ? 'Enviando...' : 'Enviar contato'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="pp-footer">
        <div className="pp-container pp-footer-inner">
          <span>usenico.com.br · Nico para parceiros</span>
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Voltar para a página do motorista</a>
        </div>
      </footer>
    </div>
  );
}
